import React from 'react';
import { Link } from 'react-router-dom';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { MenuData } from 'src/components/custom/Menu/context';
import { PATH } from 'src/constants/app';
import { RootState } from 'src/store';

import { DirectoryFilter } from 'src/store/model/role';

export type State = {
  loading: boolean;
  data: Record<string, MenuData[]>;
};

const getInitialState = (isAdmin: boolean): State => ({
  loading: false,
  data: {
    [PATH.CLASS]: [
      {
        to: '',
        key: 'undefined',
        content: 'COURSE HOME',
      },
      {
        to: 'programme-bios',
        key: 'programme-bios',
        content: 'PROGRAMME BIOS',
        type: 'CISL',
      },
      {
        to: 'modules',
        key: 'modules',
        content: 'MODULES',
      },
      {
        to: 'homework',
        key: 'homework',
        content: 'HOMEWORK HUB',
      },
      {
        to: 'notes',
        key: 'notes',
        content: 'NOTEBOOK',
      },
      {
        to: 'discussions',
        key: 'discussions',
        content: 'DISCUSSION FORUM',
      },
      {
        to: 'one_pager',
        key: 'one_pager',
        content: 'ONE PAGER',
        type: 'ACCELERATOR' as 'ACCELERATOR',
      },
      ...(isAdmin
        ? [
            {
              to: 'demo_day',
              key: 'demo_day',
              content: 'DEMO DAY',
              type: 'ACCELERATOR' as 'ACCELERATOR',
            },
          ]
        : []),
      {
        to: 'what-next',
        key: 'what-next',
        content: "WHAT'S NEXT",
        type: 'LAUNCHPAD' as 'LAUNCHPAD',
      },
    ],
    [PATH.LIBRARY]: [
      {
        to: '',
        key: 'undefined',
        content: 'All Resources',
      },
      {
        to: 'onboarding',
        key: 'onboarding',
        content: 'Onboarding',
      },
      {
        to: 'basics_101',
        key: 'basics_101',
        content: 'Basics 101',
      },
      {
        to: 'due_diligence',
        key: 'due_diligence',
        content: 'Due Diligence',
      },
      {
        to: 'investment_strategy',
        key: 'investment_strategy',
        content: 'Investment Strategy',
      },
      {
        to: 'investment_thesis',
        key: 'investment_thesis',
        content: 'Investment Thesis',
      },
      {
        to: 'linkedIn',
        key: 'linkedIn',
        content: 'LinkedIn',
      },
      {
        to: 'sourcing_startups',
        key: 'sourcing_startups',
        content: 'Sourcing Startups',
      },
      {
        to: 'true_north',
        key: 'true_north',
        content: 'True North',
      },
      {
        to: 'team_management',
        key: 'team_management',
        content: 'Team Management',
      },
      {
        to: 'valuation',
        key: 'valuation',
        content: 'Valuation',
      },
      {
        to: 'vertical_focus',
        key: 'vertical_focus',
        content: 'Vertical Focus',
      },
    ],
    [PATH.DIRECTORY]: [
      {
        to: '',
        key: 'undefined',
        content: 'All Members',
      },
      {
        to: DirectoryFilter.Experts,
        key: DirectoryFilter.Experts,
        content: 'Experts',
      },
      {
        to: DirectoryFilter.Mentors,
        key: DirectoryFilter.Mentors,
        content: 'Mentors',
      },
      {
        to: DirectoryFilter.Alumni,
        key: DirectoryFilter.Alumni,
        content: 'Alumni',
      },
    ],
    [PATH.FAQS]: [
      {
        to: '',
        key: 'undefined',
        content: 'All Questions',
      },
    ],
    [PATH.SETTING]: [
      {
        to: '',
        key: 'undefined',
        content: 'Profile',
      },
      {
        to: 'account',
        key: 'account',
        content: 'My Account',
      },
      {
        to: 'password',
        key: 'password',
        content: 'Password',
      },
    ],
  },
});

const { reducer, actions } = createSlice({
  name: 'menu',
  initialState: getInitialState(false), // Default to false, will be updated later
  reducers: {
    updateLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    updateMenu(
      state,
      action: PayloadAction<{ path: string; data: MenuData[] | MenuData }>,
    ) {
      const { data, path } = action.payload;

      if (Array.isArray(data)) {
        state.data[path] = _.unionBy(state.data[path], data, (menu) => {
          return menu.key;
        });
      } else {
        const index = state.data[path].findIndex((m) => m.key === data.key);
        state.data[path][index] = data;
      }
    },
    setAdminState(state, action: PayloadAction<boolean>) {
      state.data[PATH.CLASS] = getInitialState(action.payload).data[PATH.CLASS];
    },
  },
});

export const siderMenuSelector = createSelector(
  (state: RootState) => state.ui.menu.data,
  (state: RootState, path: string) => path,
  (menu, path) => {
    const pathname = path.split('/')[1];

    return (menu[pathname] || []).map((m) => {
      return {
        to: `/${pathname}${m.to ? `/${m.to}` : ''}`,
        key: m.key,
        content: m.content,
      };
    });
  },
);

export const settingDropdownSelector = createSelector(
  (state: RootState) => state.ui.menu.data,
  (state: RootState, path: string) => path,
  (menu, path) => {
    const pathname = path.split('/')[1];
    const menuData = menu[PATH.SETTING] || [];

    const data = menuData.map((m) => {
      const to = m.to ? `${PATH.SETTING}/${m.to}` : PATH.SETTING;

      return {
        key: m.key,
        value: m.key,
        text: React.createElement(
          Link,
          { to, className: 'd-block' },
          m.content,
        ),
      };
    });

    return {
      menu: data,
      isActive: pathname === PATH.SETTING,
    };
  },
);

export { reducer, actions };
export default actions;
